import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type {
  AnalysisDefinitionServerSnapshot,
  AnalysisDefinitionStoreActions,
  AnalysisDefinitionStoreState,
} from './types';
import type { AnalysisDefinition } from '../api/types';
import {
  addVariableSource,
  getFirstCohortProfileId,
  getOrderedSourceNames,
  moveSourceInOrder,
  removeSourceByKey,
} from '../utils/analysisSources';

const initialState: AnalysisDefinitionStoreState = {
  loadedVersionId: null,
  loadedProfileId: null,
  cohortProfileId: null,
  sources: null,
  sourceOrder: [],
  hasStagedChanges: false,
  serverSnapshot: null,
};

// Key order is not compared: the position of a source is `sourceOrder`'s job,
// and the server stores the `sources` keys sorted.
const sourcesEqual = (
  a: AnalysisDefinition['sources'],
  b: AnalysisDefinition['sources']
): boolean => {
  const byKey = (entries: [string, unknown][]) =>
    entries.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  return JSON.stringify(byKey(Object.entries(a))) === JSON.stringify(byKey(Object.entries(b)));
};

// Compares the order the list shows, so an order that only restates the
// default one is not a change.
const sourceOrdersEqual = (
  a: Pick<AnalysisDefinition, 'sources' | 'sourceOrder'>,
  b: Pick<AnalysisDefinition, 'sources' | 'sourceOrder'>
): boolean => JSON.stringify(getOrderedSourceNames(a)) === JSON.stringify(getOrderedSourceNames(b));

const isDirty = (
  serverSnapshot: AnalysisDefinitionServerSnapshot | null,
  cohortProfileId: string | null,
  sources: AnalysisDefinition['sources'],
  sourceOrder: string[]
): boolean => {
  if (!serverSnapshot) return false;
  if (serverSnapshot.cohortProfileId !== cohortProfileId) return true;
  const snapshotSources = serverSnapshot.definition?.sources ?? {};
  if (!sourcesEqual(sources, snapshotSources)) return true;
  return !sourceOrdersEqual(
    { sources, sourceOrder },
    { sources: snapshotSources, sourceOrder: serverSnapshot.definition?.sourceOrder }
  );
};

export const useAnalysisDefinitionStore = create<
  AnalysisDefinitionStoreState & AnalysisDefinitionStoreActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,
      setFromServer: ({ versionId, definition, analysisProfileId }) => {
        if (get().loadedVersionId === versionId) return;
        const cohortProfileId = getFirstCohortProfileId(definition);
        const sources = definition?.sources ?? {};
        set(
          {
            loadedVersionId: versionId,
            loadedProfileId: analysisProfileId ?? null,
            cohortProfileId,
            sources,
            sourceOrder: definition?.sourceOrder ?? [],
            hasStagedChanges: false,
            serverSnapshot: { cohortProfileId, definition },
          },
          false,
          'analysisDefinition/setFromServer'
        );
      },
      // Comparing against the snapshot lets a change-then-undo clear the dirty flag.
      setCohort: (cohortProfileId) =>
        set(
          (prev) => ({
            cohortProfileId,
            hasStagedChanges: isDirty(
              prev.serverSnapshot,
              cohortProfileId,
              prev.sources ?? {},
              prev.sourceOrder
            ),
          }),
          false,
          'analysisDefinition/setCohort'
        ),
      addSource: (avId, description) =>
        set(
          (prev) => {
            const existing = prev.sources ?? {};
            const sources = addVariableSource(existing, avId, description);
            if (sources === existing) return prev;
            return {
              sources,
              hasStagedChanges: isDirty(
                prev.serverSnapshot,
                prev.cohortProfileId,
                sources,
                prev.sourceOrder
              ),
            };
          },
          false,
          'analysisDefinition/addSource'
        ),
      removeSource: (key) =>
        set(
          (prev) => {
            const { sources, sourceOrder } = removeSourceByKey(
              prev.sources ?? {},
              prev.sourceOrder,
              key
            );
            return {
              sources,
              sourceOrder,
              hasStagedChanges: isDirty(
                prev.serverSnapshot,
                prev.cohortProfileId,
                sources,
                sourceOrder
              ),
            };
          },
          false,
          'analysisDefinition/removeSource'
        ),
      reorderSources: (activeKey, overKey) =>
        set(
          (prev) => {
            const sources = prev.sources ?? {};
            const sourceOrder = moveSourceInOrder(sources, prev.sourceOrder, activeKey, overKey);
            if (!sourceOrder) return prev;
            return {
              sourceOrder,
              hasStagedChanges: isDirty(
                prev.serverSnapshot,
                prev.cohortProfileId,
                sources,
                sourceOrder
              ),
            };
          },
          false,
          'analysisDefinition/reorderSources'
        ),
      revert: () => {
        const { serverSnapshot } = get();
        if (!serverSnapshot) return;
        set(
          {
            cohortProfileId: serverSnapshot.cohortProfileId,
            sources: serverSnapshot.definition?.sources ?? {},
            sourceOrder: serverSnapshot.definition?.sourceOrder ?? [],
            hasStagedChanges: false,
          },
          false,
          'analysisDefinition/revert'
        );
      },
      reset: () => set(initialState, false, 'analysisDefinition/reset'),
    }),
    { name: 'analysisDefinition' }
  )
);
